// Paths SVG de los iconos del sistema (estilo Tabler outline, stroke 1.5).
//
// Para AGREGAR un icono nuevo cuando aparezca en una pantalla:
//   1. Encontrá el path en docs/design-mockups/screen-*.jsx (S1Glyph, XIcon2,
//      SAIcon o L1Icon). Si no está, copialo de tabler-icons.io
//   2. Agregá una entry acá con el nombre kebab-case
//   3. El componente <Icon name="..." /> lo expone automáticamente

import type { ReactNode } from 'react';

export type IconName =
  | 'alert'
  | 'arrow-right'
  | 'arrow-left'
  | 'arrow-up'
  | 'arrow-down'
  | 'bell'
  | 'calendar'
  | 'chart-bar'
  | 'check'
  | 'chevron-down'
  | 'chevron-up'
  | 'chevron-left'
  | 'chevron-right'
  | 'clock'
  | 'dots'
  | 'download'
  | 'edit'
  | 'eye'
  | 'factory'
  | 'filter'
  | 'gauge'
  | 'home'
  | 'info'
  | 'lock'
  | 'logout'
  | 'mail'
  | 'menu'
  | 'message-circle'
  | 'minus'
  | 'plus'
  | 'refresh'
  | 'search'
  | 'settings'
  | 'shield'
  | 'sitemap'
  | 'storefront'
  | 'trash'
  | 'upload'
  | 'user'
  | 'user-cog'
  | 'users'
  | 'wrench'
  | 'x'
  // Integración con máquinas (propuesta 2026-10-alimentos-rionegro)
  | 'wifi'
  | 'wifi-off'
  | 'key'
  | 'copy'
  | 'plug'
  | 'scale'
  | 'arrows-exchange';

export const ICON_PATHS: Record<IconName, ReactNode> = {
  alert: (
    <>
      <path d="M12 4l9 16H3z" />
      <line x1="12" y1="10" x2="12" y2="14" />
      <circle cx="12" cy="17" r="0.5" />
    </>
  ),
  'arrow-right': (
    <>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="13 6 19 12 13 18" />
    </>
  ),
  'arrow-left': (
    <>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="11 18 5 12 11 6" />
    </>
  ),
  'arrow-up': (
    <>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="6 11 12 5 18 11" />
    </>
  ),
  'arrow-down': (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="6 13 12 19 18 13" />
    </>
  ),
  bell: (
    <>
      <path d="M10 5a2 2 0 014 0a7 7 0 014 6v3a4 4 0 002 3H4a4 4 0 002-3v-3a7 7 0 014-6" />
      <path d="M9 17v1a3 3 0 006 0v-1" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="9" y1="3" x2="9" y2="7" />
      <line x1="15" y1="3" x2="15" y2="7" />
    </>
  ),
  'chart-bar': (
    <>
      <line x1="4" y1="20" x2="20" y2="20" />
      <rect x="6" y="11" width="3" height="6" />
      <rect x="11" y="7" width="3" height="10" />
      <rect x="16" y="14" width="3" height="3" />
    </>
  ),
  check: <polyline points="5 12 10 17 19 7" />,
  'chevron-down': <polyline points="6 9 12 15 18 9" />,
  'chevron-up': <polyline points="6 15 12 9 18 15" />,
  'chevron-left': <polyline points="15 6 9 12 15 18" />,
  'chevron-right': <polyline points="9 6 15 12 9 18" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </>
  ),
  dots: (
    <>
      <circle cx="6" cy="12" r="0.8" fill="currentColor" />
      <circle cx="12" cy="12" r="0.8" fill="currentColor" />
      <circle cx="18" cy="12" r="0.8" fill="currentColor" />
    </>
  ),
  download: (
    <>
      <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
      <polyline points="7 11 12 16 17 11" />
      <line x1="12" y1="4" x2="12" y2="16" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4l10-10-4-4L4 16v4z" />
      <path d="M14 6l4 4" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  factory: (
    <>
      <path d="M3 21V11l5 3V11l5 3V11l5 3V21z" />
      <line x1="3" y1="21" x2="21" y2="21" />
      <line x1="9" y1="17" x2="9" y2="17" />
      <line x1="14" y1="17" x2="14" y2="17" />
    </>
  ),
  filter: <polyline points="4 4 20 4 14 12 14 19 10 21 10 12" />,
  gauge: (
    <>
      <circle cx="12" cy="13" r="8" />
      <line x1="12" y1="13" x2="16" y2="9" />
      <line x1="6" y1="4" x2="8" y2="6" />
      <line x1="18" y1="4" x2="16" y2="6" />
    </>
  ),
  home: (
    <>
      <polyline points="3 12 5 10 12 4 19 10 21 12" />
      <path d="M5 10v10h14V10" />
      <rect x="10" y="14" width="4" height="6" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <circle cx="12" cy="8" r="0.5" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </>
  ),
  logout: (
    <>
      <path d="M14 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2v-2" />
      <polyline points="9 12 21 12 18 9" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <polyline points="3 7 12 13 21 7" />
    </>
  ),
  menu: (
    <>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </>
  ),
  'message-circle': <path d="M4 18l1.4 -3.4a8 8 0 1 1 3.4 3l-4.8 .4" />,
  minus: <line x1="5" y1="12" x2="19" y2="12" />,
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  refresh: (
    <>
      <path d="M4 11A8 8 0 0118.5 7.5L21 10" />
      <polyline points="21 4 21 10 15 10" />
      <path d="M20 13a8 8 0 01-14.5 3.5L3 14" />
      <polyline points="9 20 3 20 3 14" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <line x1="20" y1="20" x2="16.5" y2="16.5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="9" strokeDasharray="2 3" />
    </>
  ),
  shield: <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />,
  sitemap: (
    <>
      <rect x="9" y="3" width="6" height="5" rx="1" />
      <rect x="3" y="16" width="6" height="5" rx="1" />
      <rect x="15" y="16" width="6" height="5" rx="1" />
      <path d="M12 8v3M6 16v-2h12v2" />
    </>
  ),
  storefront: (
    <>
      <path d="M3 9h18l-1 4a3 3 0 01-5.5 1.5 3 3 0 01-5 0A3 3 0 014 13z" />
      <path d="M5 10v10h14V10" />
    </>
  ),
  trash: (
    <>
      <polyline points="4 7 20 7" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
      <path d="M5 7l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12" />
      <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
    </>
  ),
  upload: (
    <>
      <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
      <polyline points="7 9 12 4 17 9" />
      <line x1="12" y1="4" x2="12" y2="16" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3" />
      <path d="M5 21a7 7 0 0114 0" />
    </>
  ),
  'user-cog': (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19a6 6 0 0112 0" />
      <circle cx="18" cy="14" r="2.5" />
      <path d="M18 9.5V11M18 17v1.5M22.5 14H21M15 14h-1.5" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19a6 6 0 0112 0" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M21 19a4 4 0 00-6-3.5" />
    </>
  ),
  wrench: (
    <path d="M7 10h3v-3l-3.5 -3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1 -3 3l-6 -6a6 6 0 0 1 -8 -8l3.5 3.5" />
  ),
  x: (
    <>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </>
  ),
  wifi: (
    <>
      <path d="M12 18l.01 0" />
      <path d="M9.172 15.172a4 4 0 0 1 5.656 0" />
      <path d="M6.343 12.343a8 8 0 0 1 11.314 0" />
      <path d="M3.515 9.515c4.686 -4.687 12.284 -4.687 17 0" />
    </>
  ),
  'wifi-off': (
    <>
      <path d="M12 18l.01 0" />
      <path d="M9.172 15.172a4 4 0 0 1 5.656 0" />
      <path d="M6.343 12.343a7.963 7.963 0 0 1 3.864 -2.14m4.163 .155a7.965 7.965 0 0 1 3.287 2" />
      <path d="M3.515 9.515a12 12 0 0 1 3.544 -2.455m3.101 -.92a12 12 0 0 1 10.325 3.374" />
      <path d="M3 3l18 18" />
    </>
  ),
  key: (
    <>
      <path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .008h-1.172a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.172a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0z" />
      <path d="M15 9h.01" />
    </>
  ),
  copy: (
    <>
      <path d="M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" />
      <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
    </>
  ),
  plug: (
    <>
      <path d="M9.785 6l8.215 8.215l-2.054 2.054a5.81 5.81 0 1 1 -8.215 -8.215l2.054 -2.054z" />
      <path d="M4 20l3.5 -3.5" />
      <path d="M15 4l-3.5 3.5" />
      <path d="M20 9l-3.5 3.5" />
    </>
  ),
  scale: (
    <>
      <path d="M7 20l10 0" />
      <path d="M6 6l6 -1l6 1" />
      <path d="M12 3l0 17" />
      <path d="M9 12l-3 -6l-3 6a3 3 0 0 0 6 0" />
      <path d="M21 12l-3 -6l-3 6a3 3 0 0 0 6 0" />
    </>
  ),
  'arrows-exchange': (
    <>
      <path d="M7 10h14l-4 -4" />
      <path d="M17 14h-14l4 4" />
    </>
  ),
};
